import React, { useMemo } from 'react';
import { XCircle, Minus, AlertCircle, FileText, ShieldCheck } from 'lucide-react';
import clsx from 'clsx';
import { StatusIcon } from '@components/common';
import { AssessmentItem } from '@app-types';
import styles from './AssessmentSubmissionSummary.module.css';

interface ProgressData {
  percentage: number;
  evaluated: number;
  total: number;
  implemented: number;
  implementedNeedsEvidence: number;
  gaps: number;
  notApplicable: number;
  notAssessed: number;
  remediated: number;
}

interface AssessmentSubmissionSummaryProps {
  progress: ProgressData;
  assessmentItems: AssessmentItem[];
}

const AssessmentSubmissionSummary: React.FC<AssessmentSubmissionSummaryProps> = ({ progress, assessmentItems }) => {
  // Calculate evidence requirements
  const evidenceRequired = useMemo(() => {
    return assessmentItems.filter(
      (item) =>
        (item.evaluation === 'Implemented' && item.requires_evidence) ||
        item.evaluation === 'Implemented (Needs Evidence)'
    );
  }, [assessmentItems]);

  const gapsRequiringRemediation = useMemo(() => {
    return assessmentItems.filter((item) => item.evaluation === 'Gap');
  }, [assessmentItems]);

  const implementedPercentage = progress.total > 0 ? Math.round((progress.implemented / progress.total) * 100) : 0;
  const implementedNeedsEvidencePercentage =
    progress.total > 0 ? Math.round((progress.implementedNeedsEvidence / progress.total) * 100) : 0;
  const gapsPercentage = progress.total > 0 ? Math.round((progress.gaps / progress.total) * 100) : 0;
  const notApplicablePercentage = progress.total > 0 ? Math.round((progress.notApplicable / progress.total) * 100) : 0;
  const remediatedPercentage = progress.total > 0 ? Math.round((progress.remediated / progress.total) * 100) : 0;

  return (
    <div className={styles.submissionSummary}>
      <h3 className={styles.submissionSummaryTitle}>Assessment Summary</h3>

      <div className={styles.submissionSummaryStats}>
        {/* Implemented */}
        <div className={clsx(styles.statCard, styles.statCardSuccess)}>
          <div className={styles.statCardIcon}>
            <StatusIcon status="success" size={24} />
          </div>
          <div className={styles.statCardContent}>
            <span className={styles.statCardLabel}>Implemented</span>
            <div className={styles.statCardValues}>
              <span className={styles.statCardValue}>{progress.implemented}</span>
              <span className={styles.statCardPercentage}>({implementedPercentage}%)</span>
            </div>
          </div>
        </div>

        {/* Implemented (Needs Evidence) */}
        <div className={clsx(styles.statCard, styles.statCardWarning)}>
          <div className={styles.statCardIcon}>
            <FileText size={24} />
          </div>
          <div className={styles.statCardContent}>
            <span className={styles.statCardLabel}>Implemented (Needs Evidence)</span>
            <div className={styles.statCardValues}>
              <span className={styles.statCardValue}>{progress.implementedNeedsEvidence}</span>
              <span className={styles.statCardPercentage}>({implementedNeedsEvidencePercentage}%)</span>
            </div>
          </div>
        </div>

        {/* Gaps */}
        <div className={clsx(styles.statCard, styles.statCardError)}>
          <div className={styles.statCardIcon}>
            <XCircle size={24} />
          </div>
          <div className={styles.statCardContent}>
            <span className={styles.statCardLabel}>Gaps</span>
            <div className={styles.statCardValues}>
              <span className={styles.statCardValue}>{progress.gaps}</span>
              <span className={styles.statCardPercentage}>({gapsPercentage}%)</span>
            </div>
          </div>
        </div>

        {/* Not Applicable */}
        <div className={clsx(styles.statCard, styles.statCardNeutral)}>
          <div className={styles.statCardIcon}>
            <Minus size={24} />
          </div>
          <div className={styles.statCardContent}>
            <span className={styles.statCardLabel}>Not Applicable</span>
            <div className={styles.statCardValues}>
              <span className={styles.statCardValue}>{progress.notApplicable}</span>
              <span className={styles.statCardPercentage}>({notApplicablePercentage}%)</span>
            </div>
          </div>
        </div>

        {/* Remediated */}
        {progress.remediated > 0 && (
          <div className={clsx(styles.statCard, styles.statCardInfo)}>
            <div className={styles.statCardIcon}>
              <ShieldCheck size={24} />
            </div>
            <div className={styles.statCardContent}>
              <span className={styles.statCardLabel}>Remediated</span>
              <div className={styles.statCardValues}>
                <span className={styles.statCardValue}>{progress.remediated}</span>
                <span className={styles.statCardPercentage}>({remediatedPercentage}%)</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Evidence Requirements */}
      {(evidenceRequired.length > 0 || gapsRequiringRemediation.length > 0) && (
        <div className={styles.submissionSummaryRequirements}>
          <div className={styles.requirementsHeader}>
            <AlertCircle size={20} />
            <h4>Next Steps Required:</h4>
          </div>
          <ul className={styles.requirementsList}>
            {evidenceRequired.length > 0 && (
              <li>
                <strong>{evidenceRequired.length}</strong> process{evidenceRequired.length !== 1 ? 'es' : ''} require
                evidence submission
              </li>
            )}
            {gapsRequiringRemediation.length > 0 && (
              <li>
                <strong>{gapsRequiringRemediation.length}</strong> gap{gapsRequiringRemediation.length !== 1 ? 's' : ''}{' '}
                require remediation evidence
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AssessmentSubmissionSummary;
